import { ALL_LENSES } from './data'

export function ModelGlyph({ model, size = 36, glow = true }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'Noto Serif, Georgia, serif',
        fontWeight: 700,
        fontSize: size * 0.5,
        color: model.accent,
        background: `radial-gradient(circle at 30% 25%, ${model.accent}33, ${model.accentDim}11 60%, transparent 80%), #141a32`,
        border: `1px solid ${model.accent}55`,
        boxShadow: glow
          ? `0 0 ${size * 0.5}px ${model.glow}, inset 0 0 ${size * 0.4}px ${model.accent}22`
          : 'none',
      }}
    >
      {model.glyph}
    </div>
  )
}

export function ActionChip({ children, onClick, active }) {
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={e => {
        if (active) return
        e.currentTarget.style.borderColor = 'var(--gold)'
        e.currentTarget.style.color = 'var(--text-primary)'
      }}
      onMouseLeave={e => {
        if (active) return
        e.currentTarget.style.borderColor = 'var(--glass-border)'
        e.currentTarget.style.color = 'var(--text-muted)'
      }}
      style={{
        padding: '7px 13px',
        borderRadius: 9999,
        background: active ? 'var(--gold-select)' : 'transparent',
        border: `1px solid ${active ? 'var(--gold)' : 'var(--glass-border)'}`,
        color: active ? 'var(--gold)' : 'var(--text-muted)',
        fontFamily: 'var(--font-label)',
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.08em',
        cursor: 'pointer',
        transition: 'all 200ms ease',
      }}
    >
      {children}
    </button>
  )
}

// Blinking cursor caret shown at the tail of a streaming reply. Centralized
// here so GuideColumn / Synthesize / MobileThread don't each redefine it.
// The visual is intentionally simple (rectangle, no animation) — the caret
// only appears while text is actively appending, so it blinks naturally
// because the text shifts beneath it.
export function StreamCaret({ color = 'var(--gold)', height = 13, width = 6 }) {
  return (
    <span
      style={{
        display: 'inline-block',
        width,
        height,
        marginLeft: 3,
        verticalAlign: '-2px',
        background: color,
        borderRadius: 1,
        opacity: 0.7,
      }}
    />
  )
}

// Pill-style segmented control. Used by OptionsPanel (Simple/Advanced),
// ModeBar (Free chat / Passage), MobileTopBar (same), and other 2-3 option
// togglesPropss. Pass `options` as `[{ id, label }]` and `value` as the
// active option id; calls `onChange(id)` on click. Pure visual primitive
// — owners hold their own state.
export function SegmentedControl({
  options,
  value,
  onChange,
  accent = 'var(--gold)',
  glow,
  size = 'md',
  ariaLabel,
}) {
  const tight = size === 'sm'
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      style={{
        display: 'flex',
        padding: 3,
        gap: 2,
        background: 'rgba(20,26,50,0.7)',
        border: '1px solid var(--glass-border)',
        borderRadius: 9999,
      }}
    >
      {options.map(opt => {
        const active = value === opt.id
        return (
          <button
            type="button"
            role="tab"
            aria-selected={active}
            key={opt.id}
            onClick={() => onChange?.(opt.id)}
            style={{
              padding: tight ? '6px 12px' : '8px 18px',
              borderRadius: 9999,
              border: 0,
              cursor: 'pointer',
              background: active ? accent : 'transparent',
              color: active ? 'var(--text-on-gold)' : 'var(--text-muted)',
              fontFamily: 'var(--font-body)',
              fontSize: tight ? 11.5 : 12.5,
              fontWeight: 600,
              boxShadow: active && glow ? `0 0 ${tight ? 12 : 18}px ${glow}` : 'none',
              transition: 'all 200ms',
            }}
          >
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}

export function LensUsedChip({ lensId }) {
  const lens = ALL_LENSES.find(l => l.id === lensId)
  if (!lens) return null
  return (
    <span
      style={{
        padding: '3px 8px',
        borderRadius: 9999,
        background: 'rgba(233,195,73,0.10)',
        border: '1px solid var(--gold-dim)',
        fontFamily: 'var(--font-label)',
        fontSize: 9,
        fontWeight: 600,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: 'var(--gold)',
      }}
    >
      {lens.short}
    </span>
  )
}
